import { cache } from "react";
import log from "logbeacon";
import { I18nUtil } from "./i18n";
import {
  ContentCategoryEnum,
  ContentTypeEnum,
  OpenDataTypeEnum,
  type ThemeItem,
} from "@/src/features/api/openData";
import type {
  AIInsightNewsPageData,
  DocsDetailPageServersData,
} from "@/src/screens/types";
import { getOpenDataApi } from "@/src/services/helper";
import type { NewsData } from "@/src/utils/fetchHotNews";
import { fetchNewsDetailByTitle } from "@/src/utils/fetchHotNews";
import { AIInsightTypeEnum } from "@/src/components/AIInsight/types";

import {
  buildNewsThemeGroups,
  filterNewsThemeGroups,
  type ThemeRowWithRanking,
} from "@/src/utils/newsRanking.helpers";
import { withRankingContentsTimeWindow } from "@/src/utils/rankingContentsTimeWindow";

export type {
  NewsThemeGroup,
  ThemeRowWithRanking,
} from "@/src/utils/newsRanking.helpers";

const SUB_TAG_LIST_PAGE_SIZE = 20;

/** /themes/ranking 单页 limit 上限（与 OpenAPI 一致） */
const NEWS_RANKING_THEMES_PAGE_LIMIT = 50;

/** 新闻 ranking 主题列表固定并发页数 */
const NEWS_RANKING_THEMES_MAX_PAGES = 100;

const RANKING_THEMES_FETCH_MAX_RETRIES = 3;

type NewsRankingThemeRowsRequest = {
  type: OpenDataTypeEnum;
  offset: number;
  limit: number;
  language: string;
};

/** 与 openDataApi.themes.getRankingThemes 兼容的响应壳层（含分页 total） */
type GetRankingThemesResponse = {
  data?: {
    data?: {
      rows?: ThemeRowWithRanking[];
      total?: number;
    };
  };
};

export interface FetchNewsRankingThemeRowsDeps {
  getRankingThemes: (
    params: NewsRankingThemeRowsRequest,
  ) => Promise<GetRankingThemesResponse>;
}

export interface FetchNewsRankingThemeRowsOptions {
  /** 每页条数，默认 50 */
  pageLimit?: number;
  /** 并发页数，默认 100 */
  maxPages?: number;
  /** 单页失败后的最大重试次数（不含首次请求） */
  maxRetries?: number;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

type RankingThemePageResult = {
  rows: ThemeRowWithRanking[];
  total?: number;
};

async function fetchRankingThemePageWithRetry(
  deps: FetchNewsRankingThemeRowsDeps,
  params: NewsRankingThemeRowsRequest,
  maxRetries: number,
): Promise<RankingThemePageResult> {
  let attempt = 0;
  while (attempt <= maxRetries) {
    try {
      const response = await deps.getRankingThemes(params);
      const payload = response?.data?.data;
      const rows = (payload?.rows as ThemeRowWithRanking[]) || [];
      const total =
        typeof payload?.total === "number" ? payload.total : undefined;
      return { rows, total };
    } catch (err) {
      attempt += 1;
      log.error(
        `[newsRanking] getRankingThemes offset=${params.offset} 第 ${attempt} 次失败`,
        err,
      );
      if (attempt > maxRetries) {
        return { rows: [] };
      }
      const delayMs = Math.min(200 * attempt, 1000);
      await sleep(delayMs);
    }
  }
  return { rows: [] };
}

/**
 * 先拉第一页拿到 `total`，再并发请求后续页。
 * 当接口返回有效 `total` 时，`offset >= total` 的页不再请求（避免对越界页做无意义重试）。
 * `total` 缺失时行为与原先一致：最多 maxPages 页且每页全量重试。
 */
export async function fetchAllNewsRankingThemeRows(
  deps: FetchNewsRankingThemeRowsDeps,
  language: string,
  options?: FetchNewsRankingThemeRowsOptions,
): Promise<ThemeRowWithRanking[]> {
  const pageLimit = options?.pageLimit ?? NEWS_RANKING_THEMES_PAGE_LIMIT;
  const maxPages = options?.maxPages ?? NEWS_RANKING_THEMES_MAX_PAGES;
  const maxRetries = options?.maxRetries ?? RANKING_THEMES_FETCH_MAX_RETRIES;

  const baseParams = {
    type: OpenDataTypeEnum.CONTENT,
    limit: pageLimit,
    language,
  } as const;

  const first = await fetchRankingThemePageWithRetry(
    deps,
    { ...baseParams, offset: 0 },
    maxRetries,
  );

  const merged: ThemeRowWithRanking[] = [...first.rows];
  const totalKnown = first.total;

  if (typeof totalKnown === "number" && totalKnown === 0) {
    return merged;
  }

  const restOffsets: number[] = [];
  for (let i = 1; i < maxPages; i++) {
    const offset = i * pageLimit;
    if (
      typeof totalKnown === "number" &&
      totalKnown > 0 &&
      offset >= totalKnown
    ) {
      continue;
    }
    restOffsets.push(offset);
  }

  if (restOffsets.length === 0) {
    return merged;
  }

  const restPages = await Promise.all(
    restOffsets.map(async (offset) => {
      const { rows } = await fetchRankingThemePageWithRetry(
        deps,
        { ...baseParams, offset },
        maxRetries,
      );
      return { offset, rows };
    }),
  );

  restPages.sort((a, b) => a.offset - b.offset);
  for (const p of restPages) {
    merged.push(...p.rows);
  }

  return merged;
}

export type ThemeItemWithParent = ThemeRowWithRanking & {
  parentTheme?: ThemeItem | null;
};

const getRankingThemeRows = cache(
  async (locale: string): Promise<ThemeRowWithRanking[]> => {
    const openDataApi = getOpenDataApi();
    const language = I18nUtil.getBackgroundLanguageKey(locale);
    return fetchAllNewsRankingThemeRows(
      {
        getRankingThemes: (params) =>
          openDataApi.themes.getRankingThemes(params),
      },
      language,
    );
  },
);

export const getThemeItemByThemeId = cache(
  async (
    themeId: string,
    locale: string,
  ): Promise<ThemeItemWithParent | null> => {
    if (!themeId) {
      return null;
    }
    try {
      const rows = await getRankingThemeRows(locale);
      const currentTheme = rows.find((item) => item?.id === themeId);
      if (!currentTheme) {
        return null;
      }
      const parentThemeId = currentTheme.parent_theme_id;
      const parentTheme = parentThemeId
        ? rows.find((item) => item?.id === parentThemeId) || null
        : null;

      return {
        ...currentTheme,
        parentTheme,
      };
    } catch (error) {
      log.error(
        `[getThemeItemByThemeId] fetch theme failed, themeId=${themeId}`,
        error,
      );
      return null;
    }
  },
);

/** 与 getJsonLD 中 IHomeType 一致 */
export type NewsDetailJsonLdMarketType = "crypto" | "stock";

/**
 * news/post/[title]：按父主题 slug 是否为 `crypto`（见 themes.json 顶层 Crypto）区分 crypto / 美股推荐与 JSON-LD。
 * 无父级时若当前主题为根级 Crypto（slug === "crypto"），同样视为 crypto。
 */
export function resolveNewsChannelMarketCategory(details: NewsData): {
  docsCategory: string;
  jsonLdType: NewsDetailJsonLdMarketType;
} {
  const themeItem = details.Themes?.[0]?.themeItem;
  const isCryptoByParent = themeItem?.parentTheme?.slug === "crypto";
  const isRootCryptoTheme =
    themeItem != null &&
    themeItem.parentTheme == null &&
    themeItem.slug === "crypto";
  const isCrypto = isCryptoByParent || isRootCryptoTheme;

  return {
    docsCategory: isCrypto
      ? AIInsightTypeEnum.CRYPTO
      : AIInsightTypeEnum.US_STOCK,
    jsonLdType: isCrypto ? "crypto" : "stock",
  };
}

/**
 * 与 SSR `getNewsBySubTag` / 客户端 `useHotNewsBySubTag` 共用的 `getRankingContents` 查询参数。
 */
export function buildSubTagRankingContentsParams(
  subTagId: string,
  locale: string,
  options?: { offset?: number; limit?: number },
): {
  limit: number;
  offset: number;
  language: string;
  themeIds: string[];
  type: ContentTypeEnum;
  category: ContentCategoryEnum;
  start_time: number;
  end_time: number;
} {
  const limit = options?.limit ?? SUB_TAG_LIST_PAGE_SIZE;
  const offset = options?.offset ?? 0;
  return withRankingContentsTimeWindow({
    type: ContentTypeEnum.INSIGHT,
    category: ContentCategoryEnum.STOCK,
    limit,
    offset,
    language: I18nUtil.getBackgroundLanguageKey(locale),
    themeIds: [subTagId],
  });
}

/**
 * news/post/[title] 详情：拉取正文并尝试用 token 行情 enrich（失败不影响正文）。
 */
export const getNewsDetailById = cache(
  async (
    title: string,
    locale: string,
  ): Promise<DocsDetailPageServersData | null> => {
    try {
      const openDataApiClient = getOpenDataApi();

      let result = await fetchNewsDetailByTitle(title, locale);
      if (!result) {
        result = await fetchNewsDetailByTitle(title, "en");
      }

      if (!result) {
        log.error(`[news post detail]❌ No data returned for title ${title}`);
        return null;
      }
      // const isCrypto = result?.Channel?.toLowerCase() === "crypto";
      // edgen stock、edgen crypto。 1.25 迭代, 说 result?.Channel 不准确
      const isCrypto = result?.Author?.toLowerCase() === "edgen crypto";
      const themeId = result.Themes?.[0]?.ThemeId;
      if (themeId) {
        const matchedTheme =
          (await getThemeItemByThemeId(themeId, locale)) ||
          (locale !== "en" ? await getThemeItemByThemeId(themeId, "en") : null);
        if (matchedTheme) {
          result.Themes = result.Themes.map((theme) =>
            theme.ThemeId === themeId
              ? { ...theme, themeItem: matchedTheme }
              : theme,
          );
        }
      }

      // TODO: post 语义完善后再基于真实类型选择 tokens/stocks enrich。
      const priceChangeSymbolIds = (
        result?.Tokens?.map((token) => token.SymbolId) || []
      ).slice(0, 20);

      let priceChanges: DocsDetailPageServersData["priceChanges"] = [];
      try {
        if (priceChangeSymbolIds.length > 0) {
          const baseQueryParams = {
            limit: priceChangeSymbolIds.length,
            ids: priceChangeSymbolIds,
            "enrich.enable": true,
          };

          if (isCrypto) {
            const cryptoQueryParams = {
              ...baseQueryParams,
              "enrich.with_market.enable": true,
            };
            const tokens =
              await openDataApiClient.tokens.getTokens(cryptoQueryParams);
            priceChanges = tokens?.data?.data?.rows || [];
          } else {
            const stockQueryParams = {
              ...baseQueryParams,
              "enrich.with_trading_data": true,
            };
            const stocks =
              await openDataApiClient.stocks.getStocks(stockQueryParams);
            priceChanges = stocks?.data?.data?.rows || [];
          }
        }
      } catch (err) {
        log.error(
          `[news post detail]⚠️ enrich price changes failed (title: ${title}):`,
          err,
        );
      }

      return {
        details: result,
        priceChanges,
      };
    } catch (error) {
      log.error(
        `[news post detail]❌ Error fetching news for id ${title}:`,
        error,
      );
      return null;
    }
  },
);

export const getNewsThemeGroups = cache(async (locale: string) => {
  try {
    const rows = await getRankingThemeRows(locale);
    const groups = filterNewsThemeGroups(buildNewsThemeGroups(rows));
    log.debug("[getNewsThemeGroups] groups", groups);
    return groups;
  } catch (error) {
    log.error("[getNewsThemeGroups] fetch ranking themes failed", error);
    return [];
  }
});

export const getNewsBySubTag = cache(
  async (
    subTagId: string,
    locale: string,
  ): Promise<AIInsightNewsPageData | null> => {
    const openDataApi = getOpenDataApi();
    const requestParams = buildSubTagRankingContentsParams(subTagId, locale);

    try {
      const response =
        await openDataApi.contents.getRankingContents(requestParams);
      return {
        data: response.data?.data?.rows || [],
        total: response.data?.data?.total || 0,
        pageSize: response.data?.data?.limit || 0,
      };
    } catch (error) {
      log.error("[getNewsBySubTag] fetch ranking contents failed:", error);
      return null;
    }
  },
);

export async function getDefaultSubTag(
  locale: string,
  category: string,
): Promise<string | null> {
  const groups = await getNewsThemeGroups(locale);
  const group = groups.find((item) => item.channel === category);
  return group?.subTags?.[0]?.slug || null;
}
